import * as React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { prop, theme } from 'styled-tools'
import LoadingIndicator from '../../loadingIndicator'
import Headline from '../../typography/headline'
import Paragraph from '../../typography/paragraph'
import features from '../../features'
import settings from '../../settings'
import { Row, Column } from '../../grid'
import Button from '../../button/baseButton'
import InputField from '../../customer-form/InputField'
import {
  geocodeAddress,
  retrieveAddress,
  searchAddresses,
} from '../../../../utils/badaAppointmentApi'

const InputGroup = styled(Column)`
  button {
    width: 100%;
    padding: 1px 5px;
    line-height: ${theme('forms.height')};
  }
  input {
    font-size: 16px;
    height: auto;
  }

  ${(props) => props.theme.media.min.sm`
    display: flex;
    align-items: flex-start;

    > span {
      margin-right: 10px;
      margin-bottom: 0;
      flex: 1 1 60%;
    }
    button {
      flex: 1 1 40%;
      margin:0;
      font-size: ${prop('theme.button.fontSize', '12px')};
    }
    `}
`

const StyledParagraph = styled(Paragraph)`
  font-weight: ${theme('postalCodeOverlay.text.fontWeight')};
  margin-bottom: 0;
`

const StyledHeadline = styled(Headline)`
  margin: 0;
`

const StyledColumn = styled(Column)`
  padding-top: ${theme('postalCodeOverlay.text.paddingTop')};
  padding-bottom: ${theme('postalCodeOverlay.text.paddingBottom')};
`

const StyledButton = styled(Button)``

const StyledInputField = styled(InputField)`
  height: ${theme('postalCodeOverlay.inputField.height')};
  border-radius: ${theme('postalCodeOverlay.inputField.borderRadius')};
  padding: ${theme('postalCodeOverlay.inputField.padding')};

  /* Chrome, Safari, Edge, Opera */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  &[type='number'] {
    -moz-appearance: textfield;
  }
`

const regex = new RegExp(settings.postcodeRegex)

const postcodeFormatter = (code) =>
  `${code || ''}`.replace(regex, (m, ...rest) => {
    const groups = rest.slice(0, -2).filter(Boolean)
    if (!groups.length) {
      return m
    }
    return groups.join(' ')
  })

const PostalCodeSelector = ({
  onChange,
  placeholder,
  error,
  buttonText,
  headline,
  label,
}) => {
  const [input, setInput] = React.useState('')
  const [loading, setLoading] = React.useState(false)
  const [valid, setValid] = React.useState()
  const inputRef = React.createRef()

  const searchAddress = (requirePostCode, refinementId) => {
    const postcode = postcodeFormatter(input)
    return searchAddresses({
      searchText: postcode,
      countries: settings.addressCountry || settings.countryCode,
      limit: 10,
      refinementId
    }).then((addresses) => {
      if (addresses.length < 1) {
        return undefined
      }
      const address = addresses.filter(({ type }) => type === 'Address')
      const postcodes = addresses.filter(({ type }) => type === 'Postcode')
      if (requirePostCode && postcodes.length) {
        return searchAddress(false, postcodes[0].id)
      }
      return address.length > 0
        ? address[0]
        : searchAddress(false, addresses[0].id)
    })
  }

  const findCity = (e) => {
    if (e) {
      e.preventDefault()
    }
    setLoading(true)

    if (features.requirePostCode) {
      geocodeAddress(input)
        .then((r) => {
          setValid(r.city ? undefined : false)

          if (r.city) {
            onChange({
              line1: input,
              postalCode: input,
              countryName: settings.countryCode,
              city: r.city,
              latitude: r.latitude,
              longitude: r.longitude,
            })
          }
        })
        .catch(() => {
          setValid(false)
        })
        .finally(() => {
          setLoading(false)
        })
    } else {
      searchAddress(true)
        .then((address) => {
          if (!address) {
            return undefined
          }

          return retrieveAddress(address.id)
        })
        .then((address) => {
          setValid(address ? undefined : false)
          if (address) {
            onChange(address)
          }
        })
        .catch(() => {
          setValid(false)
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }

  return (
    <form noValidate onSubmit={findCity}>
      <Row justifyContent="center">
        {!!headline && (
          <Column>
            <StyledHeadline>{headline}</StyledHeadline>
          </Column>
        )}
        {!!label && (
          <StyledColumn>
            <StyledParagraph size="large">{label}</StyledParagraph>
          </StyledColumn>
        )}
        {loading ? (
          <StyledColumn>
            <LoadingIndicator />
          </StyledColumn>
        ) : (
          <InputGroup>
            <StyledInputField
              inputRef={inputRef}
              placeholder={placeholder}
              autocomplete="postal-code"
              id="postalCode"
              value={input}
              valid={valid}
              error={error}
              disabled={loading}
              type={settings.brand === 'magnet' ? 'string' : 'number'}
              onChange={(e) => {
                setInput(e.target.value)
                setValid(undefined)
              }}
            />
            <StyledButton onClick={findCity} disabled={loading}>
              {buttonText}
            </StyledButton>
          </InputGroup>
        )}
      </Row>
    </form>
  )
}

PostalCodeSelector.propTypes = {
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
  buttonText: PropTypes.string,
  placeholder: PropTypes.string,
}

PostalCodeSelector.defaultProps = {
  error: null,
  buttonText: null,
  placeholder: null,
}

export default PostalCodeSelector
